// File: src/services/omnicine/envelopeAutoGen.ts
//
// envelopeAutoGen — OmniCine Envelope AI auto-generation (A3-safe)
// ---------------------------------------------------------------
// Emits: window.dispatchEvent(new CustomEvent("omnicine:range:suggest", { detail }))
//
// Goals:
// - Stable envelope IDs (deterministic) so TimelineShell can add/update idempotently
// - No reducer writes here (service emits intents only)
// - Conservative heuristics (works even if telemetry is missing)
// - Optional probability fields for future "quantum-weighted overlaps"
//
// Expected consumer behavior (TimelineShell):
// - If detail.id exists:
//    - if range with id exists -> dispatch("range/update")
//    - else -> dispatch("range/add") with that id
//
// Detail payload shape (compatible with your current suggest listener + future fields):
// {
//   id: string,
//   startSec: number,
//   endSec: number,
//   kind: "attention" | "emotion" | "overload" | "safety" | "music" | "selection" | "loop",
//   trackId?: string,
//   label?: string,
//   source?: "ai-assist" | "system" | "user" | "xr-user",
//   probs?: Partial<Record<"attention"|"emotion"|"overload"|"safety", number>>,
//   q?: { model?: "born" | "softmax"; temperature?: number; phaseBias?: number }
// }
//
// Usage (example):
//   import { startEnvelopeAutoGen } from "@/services/omnicine/envelopeAutoGen";
//   const stop = startEnvelopeAutoGen({
//     getState: () => (window as any).__oc_debug?.getState?.(),
//     npcTrackId: "n1",
//     cadenceMs: 250,
//     source: "ai-assist",
//   });
//   // later: stop();

export type EnvelopeKind = "attention" | "emotion" | "overload" | "safety" | "music";

export type EnvelopeSuggestDetail = {
  id: string;
  startSec: number;
  endSec: number;
  kind: EnvelopeKind;
  trackId?: string;
  label?: string;
  source?: "user" | "xr-user" | "ai-assist" | "system";
  probs?: Partial<Record<"attention" | "emotion" | "overload" | "safety", number>>;
  q?: { model?: "born" | "softmax"; temperature?: number; phaseBias?: number };
};

export type EnvelopeAutoGenOptions = {
  /**
   * How we read canonical timeline state.
   * Default: window.__oc_debug.getState() if available.
   */
  getState?: () => any;

  /**
   * Stable salt for IDs (e.g., sessionId, projectId).
   * If omitted, uses "global".
   */
  salt?: string;

  /**
   * Default NPC lane trackId (your Option 2 uses "n1").
   */
  npcTrackId?: string;

  /**
   * Emission cadence (ms). Keep modest; 150–400ms is typical.
   */
  cadenceMs?: number;

  /**
   * Source tag.
   */
  source?: EnvelopeSuggestDetail["source"];

  /**
   * Optional override for min envelope length.
   */
  minLenSec?: number;

  /**
   * Optional: enable/disable specific heuristics.
   */
  enable?: Partial<{
    npcHitAttention: boolean;
    audioMusicFromClips: boolean;
    overloadFromDenseHits: boolean;
    safetyFromLabels: boolean;
  }>;
};

const EVENT_NAME = "omnicine:range:suggest";
const DEFAULT_CADENCE = 250;
const DEFAULT_MIN_LEN = 0.25;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function isFiniteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function toNum(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * FNV-1a 32-bit hash => base36 string.
 * Deterministic, no deps, good enough for stable IDs.
 */
function fnv1aBase36(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // h *= 16777619 (via shifts)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

/**
 * Stable envelope id derived from: salt + kind + trackId + quantized times + labelKey
 * Times are quantized to 2 decimals to avoid jitter churn.
 */
function stableEnvelopeId(args: {
  salt: string;
  kind: EnvelopeKind;
  trackId?: string;
  startSec: number;
  endSec: number;
  label?: string;
}): string {
  const q = (t: number) => (Math.round(t * 100) / 100).toFixed(2);
  const key = [
    "env",
    args.salt || "global",
    args.kind,
    args.trackId || "_",
    q(args.startSec),
    q(args.endSec),
    (args.label || "").trim().slice(0, 48),
  ].join("|");
  return `env_${fnv1aBase36(key)}`;
}

/**
 * Probability normalization (softmax). Used for "quantum-weighted overlaps".
 */
function softmax4(x: [number, number, number, number], temperature = 1): [number, number, number, number] {
  const t = Math.max(1e-6, temperature);
  const m = Math.max(x[0], x[1], x[2], x[3]);
  const e0 = Math.exp((x[0] - m) / t);
  const e1 = Math.exp((x[1] - m) / t);
  const e2 = Math.exp((x[2] - m) / t);
  const e3 = Math.exp((x[3] - m) / t);
  const s = e0 + e1 + e2 + e3;
  if (s <= 0) return [0.25, 0.25, 0.25, 0.25];
  return [e0 / s, e1 / s, e2 / s, e3 / s];
}

/**
 * "Born-like" normalization: squares then normalizes.
 * Useful if upstream scores already represent amplitudes.
 */
function born4(x: [number, number, number, number]): [number, number, number, number] {
  const s0 = x[0] * x[0];
  const s1 = x[1] * x[1];
  const s2 = x[2] * x[2];
  const s3 = x[3] * x[3];
  const sum = s0 + s1 + s2 + s3;
  if (sum <= 0) return [0.25, 0.25, 0.25, 0.25];
  return [s0 / sum, s1 / sum, s2 / sum, s3 / sum];
}

function emitSuggest(detail: EnvelopeSuggestDetail) {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

/**
 * Extractors tolerate both older and newer state shapes.
 */
function readDurationSec(st: any): number {
  const d = st?.durationSec ?? st?.timeline?.durationSec ?? st?.duration ?? st?.timeline?.duration;
  return Math.max(0, toNum(d, 0));
}

function readMarkers(st: any): Array<{ id: string; timeSec: number; label?: string }> {
  const m = st?.markers ?? st?.timeline?.markers;
  return Array.isArray(m) ? m : [];
}

function readClips(st: any): Array<any> {
  const c = st?.clips ?? st?.timeline?.clips;
  return Array.isArray(c) ? c : [];
}

function isNpcMarkerLabel(label?: string): boolean {
  const s = String(label ?? "").trim().toLowerCase();
  return s.startsWith("npc:");
}

function labelAfterPrefix(label?: string): string {
  const raw = String(label ?? "").trim();
  const i = raw.indexOf(":");
  return i >= 0 ? raw.slice(i + 1).trim() : raw;
}

function includesAny(label: string, words: string[]): boolean {
  const s = label.toLowerCase();
  return words.some((w) => s.includes(w));
}

export function startEnvelopeAutoGen(opts: EnvelopeAutoGenOptions = {}) {
  const getState =
    opts.getState ??
    (() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = window as any;
      return w.__oc_debug?.getState?.();
    });

  const salt = String(opts.salt ?? "global");
  const npcTrackId = String(opts.npcTrackId ?? "n1");
  const cadenceMs = Math.max(50, toNum(opts.cadenceMs, DEFAULT_CADENCE));
  const source: EnvelopeSuggestDetail["source"] = opts.source ?? "ai-assist";
  const minLenSec = Math.max(0.05, toNum(opts.minLenSec, DEFAULT_MIN_LEN));

  const enable = {
    npcHitAttention: opts.enable?.npcHitAttention ?? true,
    audioMusicFromClips: opts.enable?.audioMusicFromClips ?? true,
    overloadFromDenseHits: opts.enable?.overloadFromDenseHits ?? true,
    safetyFromLabels: opts.enable?.safetyFromLabels ?? true,
  };

  let alive = true;
  let timer: number | null = null;

  // Basic de-dupe: only emit if signature differs.
  // (Even with stable IDs, this prevents needless churn.)
  const lastSigById = new Map<string, string>();

  const tick = () => {
    if (!alive) return;

    let st: any;
    try {
      st = getState?.();
    } catch {
      st = null;
    }

    if (!st) {
      schedule();
      return;
    }

    const durationSec = readDurationSec(st);
    if (!(durationSec > 0)) {
      schedule();
      return;
    }

    const markers = readMarkers(st);
    const clips = readClips(st);

    // ------------------------------------------------------------
    // Heuristic 1: NPC hit markers -> attention envelopes
    // ------------------------------------------------------------
    if (enable.npcHitAttention) {
      const npcHits = markers
        .filter((m) => m && isFiniteNum(m.timeSec) && isNpcMarkerLabel(m.label))
        .map((m) => ({
          id: String(m.id ?? ""),
          timeSec: toNum(m.timeSec, 0),
          label: String(m.label ?? ""),
          name: labelAfterPrefix(m.label),
        }))
        .sort((a, b) => a.timeSec - b.timeSec);

      for (const hit of npcHits) {
        // window around hit; conservative and stable
        const s0 = clamp(hit.timeSec - 0.6, 0, durationSec);
        const e0 = clamp(hit.timeSec + 1.1, 0, durationSec);
        const startSec = Math.min(s0, e0);
        const endSec = Math.max(s0, e0);
        if (endSec - startSec < minLenSec) continue;

        // Safety hints from label
        const lbl = hit.label.toLowerCase();
        const isSafety = enable.safetyFromLabels && includesAny(lbl, ["safe", "consent", "calm", "recover"]);
        const isOverload = enable.safetyFromLabels && includesAny(lbl, ["panic", "overload", "danger", "threat"]);

        const kind: EnvelopeKind = isSafety ? "safety" : isOverload ? "overload" : "attention";

        // Optional probs: attention-biased around hit
        const raw: [number, number, number, number] = [
          kind === "attention" ? 1.4 : 0.6, // attention
          0.6, // emotion
          kind === "overload" ? 1.2 : 0.4, // overload
          kind === "safety" ? 1.2 : 0.4, // safety
        ];

        const temp = 0.9;
        const p = softmax4(raw, temp);

        const probs = {
          attention: p[0],
          emotion: p[1],
          overload: p[2],
          safety: p[3],
        };

        const label = `npc:${hit.name || "hit"}`;

        const id = stableEnvelopeId({
          salt,
          kind,
          trackId: npcTrackId,
          startSec,
          endSec,
          label,
        });

        const sig = `${kind}|${startSec.toFixed(2)}|${endSec.toFixed(2)}|${label}`;
        if (lastSigById.get(id) !== sig) {
          lastSigById.set(id, sig);

          emitSuggest({
            id,
            kind,
            startSec,
            endSec,
            trackId: npcTrackId,
            label,
            source,
            probs,
            q: { model: "softmax", temperature: temp },
          });
        }
      }

      // ------------------------------------------------------------
      // Heuristic 2: Dense NPC hit clusters -> overload envelope
      // ------------------------------------------------------------
      if (enable.overloadFromDenseHits) {
        // Count hits in sliding window (~2.5s)
        const hitTimes = npcHits.map((h) => h.timeSec);
        if (hitTimes.length >= 3) {
          const win = 2.5;
          for (let i = 0; i < hitTimes.length; i++) {
            const t0 = hitTimes[i];
            let j = i;
            while (j < hitTimes.length && hitTimes[j] <= t0 + win) j++;
            const count = j - i;
            if (count >= 4) {
              const startSec = clamp(t0 - 0.4, 0, durationSec);
              const endSec = clamp(hitTimes[j - 1] + 0.8, 0, durationSec);
              if (endSec - startSec < minLenSec) continue;

              const kind: EnvelopeKind = "overload";
              const label = `npc:cluster(${count})`;

              const id = stableEnvelopeId({
                salt,
                kind,
                trackId: npcTrackId,
                startSec,
                endSec,
                label,
              });

              const sig = `${kind}|${startSec.toFixed(2)}|${endSec.toFixed(2)}|${label}`;
              if (lastSigById.get(id) !== sig) {
                lastSigById.set(id, sig);

                const raw: [number, number, number, number] = [0.35, 0.45, 1.5, 0.25];
                const model: "softmax" | "born" = "softmax";
                const temp = 0.85;
                const p = softmax4(raw, temp);

                emitSuggest({
                  id,
                  kind,
                  startSec,
                  endSec,
                  trackId: npcTrackId,
                  label,
                  source,
                  probs: { attention: p[0], emotion: p[1], overload: p[2], safety: p[3] },
                  q: { model, temperature: temp, phaseBias: 0.1 },
                });
              }

              // Skip ahead so we don’t emit multiple overlapping overloads
              i = j - 1;
            }
          }
        }
      }
    }

    // ------------------------------------------------------------
    // Heuristic 3: Audio clips -> music envelope (track-scoped if possible)
    // ------------------------------------------------------------
    if (enable.audioMusicFromClips) {
      for (const c of clips) {
        const kind = String(c?.kind ?? "").toLowerCase().trim();
        if (kind !== "audio") continue;

        const trackId = typeof c?.trackId === "string" && c.trackId.trim() ? c.trackId.trim() : undefined;

        const s0 = toNum(c?.startSec ?? c?.start, NaN);
        const e0 = toNum(c?.endSec ?? (isFiniteNum(c?.durSec) ? (toNum(c?.startSec, 0) + toNum(c?.durSec, 0)) : NaN), NaN);

        if (!Number.isFinite(s0) || !Number.isFinite(e0)) continue;

        const startSec = clamp(Math.min(s0, e0), 0, durationSec);
        const endSec = clamp(Math.max(s0, e0), 0, durationSec);
        if (endSec - startSec < minLenSec) continue;

        const envKind: EnvelopeKind = "music";
        const label = "music:bed";

        const id = stableEnvelopeId({
          salt,
          kind: envKind,
          trackId: trackId ?? "audio",
          startSec,
          endSec,
          label,
        });

        const sig = `${envKind}|${startSec.toFixed(2)}|${endSec.toFixed(2)}|${label}|${trackId ?? ""}`;
        if (lastSigById.get(id) !== sig) {
          lastSigById.set(id, sig);

          emitSuggest({
            id,
            kind: envKind,
            startSec,
            endSec,
            trackId, // if your overlay wants lane scoping for music later
            label,
            source,
          });
        }
      }
    }

    schedule();
  };

  const schedule = () => {
    if (!alive) return;
    timer = window.setTimeout(tick, cadenceMs) as unknown as number;
  };

  // start
  schedule();

  return function stop() {
    alive = false;
    if (timer != null) window.clearTimeout(timer);
    timer = null;
    lastSigById.clear();
  };
}
